import { zodResolver } from '@hookform/resolvers/zod';
import { extractTextWithNewLinesFromHtml } from '@talentmesh/rte';
import { useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { useCompanyContext } from '@/Context/CompanyContext';
import useCompanyId from '@/Hooks/UseCompanyId';
import { CompanyProfile } from '@/Models/CompanyProfile';
import UIStrings from '@/Utils/UIStrings';

const companyProfileField = (limitExceedMessage: string) =>
    z.string().min(1, UIStrings.Required).max(128, limitExceedMessage);

export const companyProfileSchema = z.object({
    companyName: companyProfileField(UIStrings.CompanyNameInputLengthValidation),
    companySlug: companyProfileField(UIStrings.CompanySlugInputLengthValidation).regex(
        /^[a-z0-9]+(-[a-z0-9]+)*$/, // validate that slug consists of lowercase letters, numbers and dashes only
        UIStrings.CompanySlugInvalidFormat,
    ),
    companyDescription: z
        .string()
        .refine((value) => extractTextWithNewLinesFromHtml(value || '').length !== 0, UIStrings.Required)
        .refine(
            (value) => extractTextWithNewLinesFromHtml(value || '').length < 4000,
            UIStrings.CompanyDescriptionInputLengthValidation,
        ),
});

export type EditCompanyProfileForm = z.infer<typeof companyProfileSchema>;

export const useCompanyProfileForm = () => {
    const companyId = useCompanyId();
    const { currentCompanyProfile, refreshCurrentCompanyProfile } = useCompanyContext();
    const [fetchError, setFetchError] = useState<unknown>(null);
    const form = useForm<EditCompanyProfileForm>({
        resolver: zodResolver(companyProfileSchema),
        defaultValues: async () => {
            try {
                const result = await refreshCurrentCompanyProfile(companyId);

                return result;
            } catch (e) {
                setFetchError(e);
            }

            return currentCompanyProfile;
        },
    });

    return { ...form, fetchError };
};

export const useCompanyProfileFormContext = () => useFormContext<CompanyProfile>();
